import React, { useState } from 'react';
import {
  X,
  Plus,
  Layers,
  Search,
  Sparkles,
  Layout,
  BookOpen,
  Trophy,
  Package,
  Send,
  HelpCircle,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { SECTION_PRESETS, type SectionPreset } from '../templates/sectionPresets';
import type { BlockNode } from '../types';

export interface SectionPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSection: (section: SectionPreset) => void;
  insertIndex?: number;
}

const CATEGORY_TABS: Array<{ id: string; label: string; icon: React.ElementType }> = [
  { id: 'all', label: 'All Sections', icon: Sparkles },
  { id: 'canonical', label: '⭐ Core 8 Sections', icon: Sparkles },
  { id: 'heroes', label: 'Heroes', icon: Layout },
  { id: 'features', label: 'Features', icon: Layout },
  { id: 'courses', label: 'Courses', icon: BookOpen },
  { id: 'faculty', label: 'Faculty & Tutors', icon: Users },
  { id: 'results', label: 'Testimonials & Results', icon: Trophy },
  { id: 'faq_notices', label: 'FAQ', icon: HelpCircle },
  { id: 'lead_forms', label: 'Contact', icon: Send },
  { id: 'cta', label: 'Call-to-Action', icon: Sparkles },
  { id: 'store', label: 'Digital Store', icon: Package },
  { id: 'headers', label: 'Headers', icon: Layout },
  { id: 'footers', label: 'Footers', icon: ShieldCheck },
];

export function SectionPickerModal({
  isOpen,
  onClose,
  onSelectSection,
  insertIndex,
}: SectionPickerModalProps): React.JSX.Element | null {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  if (!isOpen) return null;

  const filteredSections = SECTION_PRESETS.filter((sec) => {
    const matchesCategory =
      activeCategory === 'all'
        ? true
        : activeCategory === 'canonical'
        ? !!sec.isCanonical
        : sec.category === activeCategory;
    const matchesSearch =
      sec.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sec.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sec.categoryLabel.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div
        className="bg-card border border-border rounded-2xl max-w-4xl w-full h-[85vh] max-h-[760px] shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-4 md:p-5 border-b border-border flex items-center justify-between bg-muted/20 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">Turnkey Section Library</h2>
              <p className="text-xs text-muted-foreground">
                Click any pre-designed coaching academy section to insert it instantly into your website.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Search & Category Filter Bar */}
        <div className="px-5 py-3 border-b border-border bg-card/60 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          {/* Category Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar w-full sm:w-auto pb-1 sm:pb-0">
            {CATEGORY_TABS.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-64 shrink-0">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search sections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-input bg-background text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-none"
            />
          </div>
        </div>

        {/* Section Cards Grid */}
        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSections.map((sec) => (
            <div
              key={sec.id}
              className="rounded-xl border border-border bg-card hover:border-primary/60 transition-all flex flex-col justify-between overflow-hidden shadow-xs hover:shadow-md group"
            >
              {/* Visual Header / Banner */}
              <div
                className={`h-24 bg-gradient-to-r ${sec.previewColor} p-4 flex flex-col justify-between text-white relative overflow-hidden`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-black/20 backdrop-blur-xs">
                      {sec.categoryLabel}
                    </span>
                    {sec.isCanonical && (
                      <span className="text-[9px] uppercase font-extrabold tracking-wider px-1.5 py-0.5 rounded bg-amber-400 text-slate-900 shadow-xs">
                        Core
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded font-mono">
                    {sec.blocks.length} block{sec.blocks.length > 1 ? 's' : ''}
                  </span>
                </div>
                <h3 className="font-bold text-sm tracking-tight drop-shadow-sm">{sec.title}</h3>
              </div>

              {/* Description & Action */}
              <div className="p-4 flex-1 flex flex-col justify-between">
                <p className="text-xs text-muted-foreground leading-relaxed mb-4">{sec.description}</p>
                <button
                  type="button"
                  onClick={() => onSelectSection(sec)}
                  className="w-full py-2 px-3 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground font-bold text-xs transition-colors flex items-center justify-center gap-1.5 cursor-pointer shadow-xs min-h-[38px]"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Insert Section</span>
                </button>
              </div>
            </div>
          ))}

          {filteredSections.length === 0 && (
            <div className="col-span-full py-16 text-center text-muted-foreground flex flex-col items-center justify-center gap-2">
              <Layers className="h-8 w-8 opacity-40" />
              <p className="text-sm font-semibold">No section templates match your search</p>
              <button
                type="button"
                onClick={() => {
                  setActiveCategory('all');
                  setSearchQuery('');
                }}
                className="text-xs text-primary underline mt-1"
              >
                Reset filters
              </button>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-3 border-t border-border bg-muted/20 px-5 flex items-center justify-between text-xs text-muted-foreground shrink-0">
          <span>{filteredSections.length} turnkey sections ready to use</span>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-border hover:bg-muted text-foreground font-medium cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
